/** Regions / environments — keep in sync with config/ingestion.yaml. */
export const DEFAULT_REGIONS = ['us', 'eu', 'ca'];
export const DEFAULT_ENVIRONMENTS = ['staging', 'preview', 'production'];

/**
 * Region encoded in a job id (same rules as backend/lib/conventions.js).
 *   us-staging-cards.platform2 → us
 *   ca-staging-safety.vg5.*    → ca
 *   sqa-api-tests-fleet        → us (config-declared demos are the US pipeline)
 */
export function regionOfJobId(jobId) {
  const s = String(jobId || '');
  const m = /^([a-z]{2})-([a-z0-9]+)-(.+)$/.exec(s);
  if (m && DEFAULT_REGIONS.includes(m[1])) return m[1];
  return 'us';
}

/**
 * Environment encoded in a job id (middle segment of `<region>-<env>-<suite>`).
 *   us-staging-cards.platform2 → staging
 *   eu-preview-fleetapp        → preview
 *   sqa-api-tests-fleet        → staging
 */
export function environmentOfJobId(jobId) {
  const s = String(jobId || '');
  const m = /^([a-z]{2})-([a-z0-9]+)-(.+)$/.exec(s);
  if (m && DEFAULT_REGIONS.includes(m[1])) return m[2];
  return 'staging';
}

/** Annotate jobs with derived region/environment from the id prefix. */
export function annotateJobs(jobs) {
  return (jobs || []).map((j) => ({
    ...j,
    region: j.region || regionOfJobId(j.job_id),
    // Prefer id-derived env for filter identity; run-level env on the job is
    // often the same, but preview/prod prefixes must win for TopBar filtering.
    environment: environmentOfJobId(j.job_id) || j.environment || 'staging',
  }));
}

/** Filter the catalog by region and (optional) environment id prefixes. */
export function jobsForFilters(jobs, { region = 'us', environment = '' } = {}) {
  const r = String(region || 'us').toLowerCase();
  const e = String(environment || '').toLowerCase();
  return annotateJobs(jobs).filter((j) => {
    if (String(j.region).toLowerCase() !== r) return false;
    if (e && String(j.environment).toLowerCase() !== e) return false;
    return true;
  });
}

/** @deprecated use jobsForFilters — kept for existing imports/tests */
export function jobsForRegion(jobs, region) {
  return jobsForFilters(jobs, { region, environment: '' });
}

/**
 * Pick a valid job for the filtered list.
 * Prefers the current job, then a remembered job for this slice, then the first.
 */
export function pickJobForRegion(jobs, { jobId, rememberedJobId } = {}) {
  if (!jobs?.length) return null;
  return jobs.find((j) => j.job_id === jobId)
    || jobs.find((j) => j.job_id === rememberedJobId)
    || jobs[0];
}
